<template>
  <div>
    <LoadingWrapper :loading="loading">
      <template #loading>
        <Loading :smBreakItem="smBreakItem" :lgBreakItem="lgBreakItem" />
      </template>
      <template #default>
        <b-row class="margin-0">
          <b-col cols="12" class="flex-between margin-bottom-16 padding-0">
            <div
              class="font-size-14 color-complementary-400 mobile-none tablet-none"
            >
              <span> {{ label }} </span>
              <span v-if="pagingData.total">
                <span class="color-complementary-100"> | </span>
                <span class="color-complementary-200">
                  {{ pagingData.total }}
                </span>
              </span>
            </div>
            <Button
              variant="primary"
              :text="$lang.adding"
              rightIcon="plus"
              wrapperClass="mobile-width-percent-100 tablet-width-percent-100"
              contentClass="justify-content-center"
              :disabled="isDisabledAddButton"
              @click="handleOpenAddAttachmentPin"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <LazyLoadingWrapper
              :wrapperClass="`border--solid border--complementary-50 border-radius-8 padding-top-16 padding-x-8 background-surface min-height-300 overflow-y-auto ${wrapperClass}`"
              emptyBoxClass="z-index-1"
              :loading="loadMoreLoading"
              :loadMore="loadMore"
              :emptyImage="emptyImage"
              :emptyText="emptyText"
              :isEmpty="isFirstLoaded && !items.length && !loading"
              scrollDistance="2000"
            >
              <template>
                <b-row class="margin-0 position-relative overflow-y-auto">
                  <b-col
                    v-for="(item, index) in items"
                    :key="`attachment-${index}`"
                    cols="12"
                    :sm="smBreakItem"
                    :lg="lgBreakItem"
                    class="margin-bottom-16 padding-x-8"
                  >
                    <FileBox :file="item" />
                  </b-col>
                </b-row>
              </template>
            </LazyLoadingWrapper>
          </b-col>
        </b-row>
      </template>
    </LoadingWrapper>
    <AddAttachmentPin
      :title="$lang.newAttachment"
      :visible="isAddAttachmentPin"
      :submitMethod="createMethod"
      :uploaderName="uploaderName"
      :currentFiles="items"
      :width="AddAttachmentSidebarWidth"
      @handleClose="handleCloseAddAttachmentPin"
      @handleReload="fetchData"
    />
  </div>
</template>
<script>
import { LoadingWrapper } from "@/components/re-design";
import { Button } from "@/components/re-design/forms";
import LazyLoadingWrapper from "@/components/re-design/lazy-loading-wrapper/LazyLoadingWrapper.vue";
import Loading from "./Loading.vue";
import FileBox from "./FileBox.vue";
import AddAttachmentPin from "./AddAttachmentPin.vue";
import { computed, onMounted, provide, ref, toRefs } from "vue";
import {
  generateConfig,
  generateError,
  generateLang,
  storeDispatcher,
} from "@/shared/utils";
export default {
  components: {
    LoadingWrapper,
    LazyLoadingWrapper,
    Button,
    Loading,
    FileBox,
    AddAttachmentPin,
  },
  props: {
    label: {
      type: String,
      default: "",
    },
    getListMethod: {
      type: Function,
      default: () => {},
    },
    createMethod: {
      type: Function,
      default: () => {},
    },
    updateMethod: {
      type: Function,
      default: () => {},
    },
    deleteMethod: {
      type: Function,
      default: () => {},
    },
    defaultPerPage: {
      type: [Number, String],
      default: 20,
    },
    uploaderName: {
      type: String,
      default: "",
    },
    shouldEditableAttachmentType: {
      type: Boolean,
      default: true,
    },
    isDisabledAddButton: {
      type: Boolean,
      default: false,
    },
    smBreakItem: {
      type: String,
      default: "6",
    },
    lgBreakItem: {
      type: String,
      default: "3",
    },
    wrapperClass: {
      type: String,
      default: "",
    },
    AddAttachmentSidebarWidth: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props, { emit }) {
    // bind props
    const {
      createMethod,
      updateMethod,
      deleteMethod,
      defaultPerPage,
      shouldEditableAttachmentType,
    } = toRefs(props);

    const emptyImage = computed(() =>
      generateConfig(["images", "shared", "redesign", "empty", "list"])
    );
    const emptyText = computed(() => generateLang(["list_empty_text"]));

    const isFirstLoaded = ref(false);
    const loading = ref(false);
    const loadMoreLoading = ref(false);
    const others = ref(null);
    const permissions = ref([]);
    const pagingData = ref({
      total: 0,
      count: 0,
      totalPages: 1,
      currentPage: 1,
      perPage: defaultPerPage.value,
    });
    const items = ref([]);

    const getList = async () => {
      // base method for get data list
      try {
        const { currentPage: page, perPage } = pagingData.value;
        const response = await props.getListMethod(page, perPage);
        const {
          total,
          count,
          totalPages,
          currentPage,
          permissions: getPermissions,
          others: getOthers,
          items: getItems,
        } = response;

        pagingData.value = {
          ...pagingData.value,
          total,
          count,
          totalPages,
          currentPage,
        };
        permissions.value = getPermissions;
        others.value = getOthers;

        const items = getItems.map((item) => {
          return { ...item };
        });

        return items;
      } catch (error) {
        generateError(error);
      }
    };
    const fetchData = async () => {
      // get first data
      if (loading.value) return;
      items.value = [];
      try {
        loading.value = true;
        const firstItems = await getList();
        items.value = firstItems;
        await storeDispatcher("mobileHeaderTitleCount", paging.total);
      } catch (error) {
        generateError(error);
      } finally {
        loading.value = false;
        isFirstLoaded.value = true;
      }
    };
    const loadMore = async () => {
      // load more data and pushed to list
      if (loading.value || loadMoreLoading.value) return;
      const { currentPage, totalPages } = pagingData.value;
      if (currentPage >= totalPages) return;

      try {
        loadMoreLoading.value = true;
        pagingData.value.currentPage += 1;
        const newItems = await getList();
        items.value.push(...newItems);
      } catch (error) {
        generateError(error);
      } finally {
        loadMoreLoading.value = false;
      }
    };
    const handleReloadList = () => fetchData();
    onMounted(() => fetchData());

    const isAddAttachmentPin = ref(false);
    const handleOpenAddAttachmentPin = () => (isAddAttachmentPin.value = true);
    const handleCloseAddAttachmentPin = () =>
      (isAddAttachmentPin.value = false);

    // provide data
    provide(
      "permissions",
      computed(() => permissions.value)
    );
    provide(
      "SHOULD_EDITABLE_ATTACHMENT_TYPE",
      computed(() => shouldEditableAttachmentType.value)
    );
    provide("handleReloadList", handleReloadList);
    provide("updateMethod", updateMethod.value);
    provide("deleteMethod", deleteMethod.value);

    // return data to be used in template
    return {
      emptyImage,
      emptyText,
      isFirstLoaded,
      loadMoreLoading,
      loading,
      fetchData,
      loadMore,
      pagingData,
      permissions,
      items,
      isAddAttachmentPin,
      handleOpenAddAttachmentPin,
      handleCloseAddAttachmentPin,
      createMethod,
    };
  },
};
</script>
